import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { Content, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { Box, Button, Chip, Typography, makeStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles(theme => ({
  running:      { backgroundColor: '#228722', color: '#fff', height: 22, fontSize: '0.75rem' },
  provisioning: { backgroundColor: '#527EDB', color: '#fff', height: 22, fontSize: '0.75rem' },
  failed:       { backgroundColor: '#cd3c14', color: '#fff', height: 22, fontSize: '0.75rem' },
  deleting:     { backgroundColor: '#cd3c14', color: '#fff', height: 22, fontSize: '0.75rem' },
  unknown:      { backgroundColor: theme.palette.grey[500], color: '#fff', height: 22, fontSize: '0.75rem' },
  header:       { marginBottom: theme.spacing(2) },
}));

interface CapiMachine {
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp?: string;
    labels?: Record<string, string>;
  };
  spec?: { version?: string };
  status?: {
    phase?: string;
    nodeRef?: { name?: string };
    addresses?: Array<{ type: string; address: string }>;
    failureReason?: string;
    failureMessage?: string;
  };
}

interface CapiMachineList {
  items: CapiMachine[];
}

type MachineRow = {
  name: string;
  phase: string;
  roles: string;
  version: string;
  nodeRef: string;
  internalIp: string;
  age: string;
  failureReason: string;
};

function formatAge(timestamp?: string): string {
  if (!timestamp) return '—';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

type PhaseClass = 'running' | 'provisioning' | 'failed' | 'deleting' | 'unknown';

function machineRoles(labels: Record<string, string>): string {
  if ('cluster.x-k8s.io/control-plane' in labels) return 'control-plane, etcd';
  return 'compute';
}

function parseMachine(m: CapiMachine): MachineRow {
  return {
    name: m.metadata.name,
    phase: m.status?.phase ?? 'Unknown',
    roles: machineRoles(m.metadata.labels ?? {}),
    version: m.spec?.version ?? '—',
    nodeRef: m.status?.nodeRef?.name ?? '—',
    internalIp: (m.status?.addresses ?? []).find(a => a.type === 'InternalIP')?.address ?? '—',
    age: formatAge(m.metadata.creationTimestamp),
    failureReason: m.status?.failureReason ?? '',
  };
}

type Props = {
  mgmtClusterName: string;
  capiClusterName: string;
  onBack: () => void;
};

export const ManagedClusterDetailPanel = ({ mgmtClusterName, capiClusterName, onBack }: Props) => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);

  const [rows, setRows] = useState<MachineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const labelSelector = encodeURIComponent(`cluster.x-k8s.io/cluster-name=${capiClusterName}`);
      const resp = await kubernetesApi.proxy({
        clusterName: mgmtClusterName,
        path: `/apis/cluster.x-k8s.io/v1beta1/machines?labelSelector=${labelSelector}`,
      });
      if (!resp.ok) {
        throw new Error(`Kubernetes API returned ${resp.status}`);
      }
      const body = (await resp.json()) as CapiMachineList;
      setRows((body.items ?? []).map(parseMachine));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch machines');
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi, mgmtClusterName, capiClusterName]);

  useEffect(() => { load(); }, [load]);

  const phaseChipClass = (phase: string): string => {
    const key = phase.toLowerCase() as PhaseClass;
    return classes[key] ?? classes.unknown;
  };

  const columns: TableColumn<MachineRow>[] = [
    {
      title: 'Name',
      field: 'name',
      render: r => <span style={{ fontFamily: 'monospace' }}>{r.name}</span>,
    },
    {
      title: 'Phase',
      field: 'phase',
      render: r => <Chip label={r.phase} className={phaseChipClass(r.phase)} />,
    },
    { title: 'Roles', field: 'roles' },
    { title: 'Version', field: 'version' },
    {
      title: 'Node',
      field: 'nodeRef',
      render: r => <span style={{ fontFamily: 'monospace' }}>{r.nodeRef}</span>,
    },
    {
      title: 'Internal IP',
      field: 'internalIp',
      render: r => <span style={{ fontFamily: 'monospace' }}>{r.internalIp}</span>,
    },
    { title: 'Age', field: 'age' },
    {
      title: 'Failure Reason',
      field: 'failureReason',
      render: r => r.failureReason ? <span style={{ color: '#cd3c14' }}>{r.failureReason}</span> : <span style={{ color: '#aaa' }}>—</span>,
    },
  ];

  return (
    <Content>
      <Box className={classes.header} display="flex" alignItems="center" style={{ gap: 12 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
          Managed Clusters
        </Button>
        <Typography variant="h5" style={{ fontFamily: 'monospace' }}>
          {capiClusterName}
        </Typography>
      </Box>
      {error && <WarningPanel title="Failed to fetch machines" message={error} />}
      <Table<MachineRow>
        title={`Machines (${rows.length})`}
        isLoading={loading}
        data={rows}
        columns={columns}
        options={{ paging: false, search: false }}
      />
    </Content>
  );
};
